import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Play, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FadeUp, MaskLines, SectionLabel } from "./Reveal";
import { api, fileUrl } from "@/lib/api";
import { GALLERY_CATEGORIES } from "@/lib/data";
import { useLang } from "@/lib/i18n";

const Tile = ({ m, onOpen, i }) => {
  const { t } = useLang();
  const cat = t.gallery.cats[m.category] || m.category;
  return (
  <FadeUp delay={(i % 5) * 0.05}>
    <motion.button data-testid={`gallery-item-${m.id}`} onClick={() => onOpen(m)} whileHover="hover" className="group relative block aspect-[4/5] w-full overflow-hidden bg-coal text-left">
      {m.media_type === "video" ? (
        <img src={m.thumb} alt={m.title} loading="lazy" className="h-full w-full object-cover object-top" />
      ) : (
        <motion.img variants={{ hover: { scale: 1.06 } }} transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }} src={m.thumb} alt={m.title} loading="lazy" decoding="async" className="h-full w-full object-cover object-top" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-transparent to-transparent" />
      {m.media_type === "video" && (
        <span className="absolute left-1/2 top-1/2 grid h-12 w-12 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-cream/40 bg-forest/50 text-cream backdrop-blur-md transition-transform duration-500 group-hover:scale-110"><Play className="ml-1 h-5 w-5" /></span>
      )}
      <div className="absolute inset-x-0 bottom-0 p-3 md:p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#F2C14E] md:text-[11px]">{cat}</div>
        <div className="font-display mt-1 text-xs font-extrabold uppercase leading-tight text-cream md:text-sm">{m.title}</div>
      </div>
    </motion.button>
  </FadeUp>
  );
};

const PAGE = 15;

export const Gallery = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [limit, setLimit] = useState(PAGE);
  const [open, setOpen] = useState(null);
  const { t } = useLang();
  const g = t.gallery;

  useEffect(() => {
    api.get("/media")
      .then((r) => setItems(Array.isArray(r.data) ? r.data.map((m) => ({ ...m, src: fileUrl(m.storage_path), thumb: fileUrl(m.thumb_path || m.storage_path) })) : []))
      .catch(() => setItems([]));
  }, []);

  const shown = useMemo(() => (filter === "All" ? [...items].sort((a, b) => (a.media_type === "video") - (b.media_type === "video") || (b.rank ?? 0) - (a.rank ?? 0)) : items.filter((m) => m.category === filter)), [items, filter]);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative bg-coal py-24 md:py-36">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <SectionLabel n="04">{g.label}</SectionLabel>
            <h2 className="font-display mt-8 text-4xl font-black uppercase leading-[0.92] text-bone sm:text-5xl lg:text-6xl">
              <MaskLines inView lines={[g.l1, <span key="e" className="font-serif-i normal-case text-brand">{g.l2}</span>]} />
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" role="tablist">
            {GALLERY_CATEGORIES.map((c) => (
              <button key={c} data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => { setFilter(c); setLimit(PAGE); }} className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${filter === c ? "border-brand bg-brand text-ink" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>
                {g.cats[c] || c}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-14 grid grid-cols-2 gap-2 sm:grid-cols-3 md:gap-3 lg:grid-cols-5">
          {shown.slice(0, limit).map((m, i) => <Tile key={m.id} m={m} i={i} onOpen={setOpen} />)}
        </div>
        {shown.length > limit && (
          <div className="mt-10 flex justify-center">
            <button type="button" data-testid="gallery-load-more" onClick={() => setLimit((l) => l + PAGE)} className="border border-brand px-8 py-4 font-display text-sm font-extrabold uppercase tracking-[0.12em] text-brand transition-colors duration-300 hover:bg-brand hover:text-ink">
              {g.more} ({shown.length - limit})
            </button>
          </div>
        )}
        {!shown.length && <p data-testid="gallery-empty" className="mt-10 font-mono text-sm text-zinc-400">{g.empty}</p>}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent aria-describedby={undefined} data-testid="gallery-lightbox" className="max-w-5xl border-white/10 bg-ink p-0 [&>button]:hidden">
          <DialogTitle className="sr-only">{open?.title}</DialogTitle>
          {open && (
            <div className="relative">
              {open.media_type === "video" ? (
                <video src={open.src} poster={open.thumb} controls muted autoPlay playsInline loop controlsList="nodownload" className="max-h-[80vh] w-full bg-ink" />
              ) : (
                <img src={open.src} alt={open.title} className="max-h-[80vh] w-full object-contain" />
              )}
              <div className="flex items-center justify-between gap-4 p-5">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand">{g.cats[open.category] || open.category}</div>
                  <div className="font-display mt-1 text-lg font-extrabold uppercase text-bone">{open.title}</div>
                  {open.description && <p className="mt-1 text-sm text-zinc-300">{open.description}</p>}
                </div>
                <button data-testid="gallery-lightbox-close" onClick={() => setOpen(null)} className="grid h-10 w-10 place-items-center border border-white/15 text-bone hover:border-brand"><X className="h-4 w-4" /></button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
